import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Truck, Download, StickyNote, Search, Image as ImageIcon } from "lucide-react";
import { ProofViewer } from "./ProofViewer";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, errMsg, money, orderRef, PLATFORMS } from "../../lib/api";

const STATUSES = [
  ["awaiting_payment", "Awaiting payment"],
  ["paid", "Paid"],
  ["delivered", "Delivered"],
  ["cancelled", "Cancelled"],
];
const METHOD = { stripe: "Card", applepay: "Apple Pay", paypal: "PayPal", crypto: "Crypto", cashapp: "Cash App", manual: "Contact" };

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);

const ExportBar = () => {
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/orders/export", { params: { start, end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ogm-orders-${start}-to-${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.status === 400 ? "End date must be after start date" : errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const inp = "h-10 border border-white/15 bg-black/40 px-3 font-mono text-xs text-white [color-scheme:dark] focus:border-[#a259ff] focus:outline-none";
  return (
    <div className="mt-6 flex flex-wrap items-end gap-3" data-testid="admin-export-bar">
      <label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">From<input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className={`${inp} mt-1.5 block`} data-testid="export-start-input" /></label>
      <label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">To<input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className={`${inp} mt-1.5 block`} data-testid="export-end-input" /></label>
      <button onClick={download} disabled={busy || !start || !end} data-testid="export-download-button" className="flex h-10 items-center gap-2 border border-[#a259ff] px-4 font-mono text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#a259ff] hover:text-black disabled:opacity-50">
        <Download className="h-4 w-4" /> {busy ? "Preparing…" : "Download spreadsheet"}
      </button>
      <span className="font-mono text-[11px] text-slate-500">Opens in Excel, Google Sheets or Numbers · dates in UTC</span>
    </div>
  );
};

export const OrdersTab = ({ onChange }) => {
  const [orders, setOrders] = useState([]);
  const [delivering, setDelivering] = useState(null);
  const [details, setDetails] = useState("");
  const [noting, setNoting] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewing, setViewing] = useState(null);

  const term = q.trim().toLowerCase();
  const shown = orders.filter((o) => (statusFilter === "all" || o.status === statusFilter) &&
    (!term || [orderRef(o.id), o.email, o.contact, o.product_name, o.notes, o.discount_code, METHOD[o.payment_method]].join(" ").toLowerCase().includes(term)));

  const saveNotes = async () => {
    try {
      const { data } = await api.patch(`/admin/orders/${noting.id}/notes`, { notes: noteText });
      setOrders((list) => list.map((o) => (o.id === data.id ? data : o)));
      toast.success("Note saved");
      setNoting(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    api.get("/admin/orders").then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => setOrders([]));
  }, []);

  const update = async (id, status, delivery_details) => {
    try {
      const { data } = await api.patch(`/admin/orders/${id}`, { status, ...(delivery_details !== undefined && { delivery_details }) });
      setOrders((list) => list.map((o) => (o.id === id ? data : o)));
      toast.success(status === "delivered" ? "Delivered — buyer has been emailed" : "Order updated");
      setDelivering(null);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const openDeliver = (o) => {
    setDetails(o.delivery_details ?? "");
    setDelivering(o);
  };

  const onStatus = (o, v) => (v === "delivered" ? openDeliver(o) : update(o.id, v));

  if (!orders.length) return <><ExportBar /><p className="mt-6 text-slate-400" data-testid="admin-orders-empty">No orders yet.</p></>;

  return (
    <>
    <ExportBar />
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center" data-testid="admin-orders-filters">
      <div className="relative flex-1 sm:max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, email, handle, account, note…" data-testid="admin-orders-search" className="h-10 w-full border border-white/15 bg-black/40 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-[#a259ff] focus:outline-none" />
      </div>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="h-10 w-full rounded-none border-white/15 bg-transparent text-xs sm:w-48" data-testid="admin-orders-status-filter"><SelectValue /></SelectTrigger>
        <SelectContent className="border-white/10 bg-[#0f0f16] text-white">
          <SelectItem value="all">All statuses</SelectItem>
          {STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="admin-orders-count">{shown.length} of {orders.length}</span>
    </div>
    <div className="mt-4 overflow-x-auto border border-white/10">
      <Table data-testid="admin-orders-table">
        <TableHeader>
          <TableRow className="border-white/10">
            {["Ref", "Date", "Account", "Buyer", "Method", "Total", "Status"].map((h) => (
              <TableHead key={h} className="font-mono text-[11px] uppercase tracking-widest text-slate-400">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((o) => (
            <TableRow key={o.id} className="border-white/10" data-testid={`admin-order-row-${o.id}`}>
              <TableCell className="font-mono text-xs">{orderRef(o.id)}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-slate-300">{new Date(o.created_at).toLocaleString()}</TableCell>
              <TableCell className="text-sm">{o.product_name} <span className="text-slate-400">· {PLATFORMS[o.platform]?.label}</span>
                {o.notes && <p className="mt-1 max-w-[220px] truncate text-xs text-violet-300" title={o.notes} data-testid={`admin-order-note-preview-${o.id}`}>{o.notes}</p>}
              </TableCell>
              <TableCell className="text-xs"><div>{o.email}</div><div className="text-slate-400">{o.contact}</div></TableCell>
              <TableCell className="text-xs">{METHOD[o.payment_method]}
                {o.proofs?.length > 0 && (
                  <button onClick={() => setViewing(o)} data-testid={`admin-order-proof-${o.id}`} className="mt-1 flex items-center gap-1 text-[11px] text-[#c084fc] underline-offset-2 hover:underline">
                    <ImageIcon className="h-3 w-3" /> {o.proofs.length} proof{o.proofs.length > 1 ? "s" : ""}
                  </button>
                )}
              </TableCell>
              <TableCell className="font-mono text-sm">{money(o.amount)}{o.discount_code && <div className="text-[11px] text-[#e9d5ff]">{o.discount_code}</div>}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                <Select value={o.status} onValueChange={(v) => onStatus(o, v)}>
                  <SelectTrigger className="h-9 w-40 border-white/15 bg-transparent text-xs" data-testid={`admin-order-status-${o.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0f0f16] text-white">
                    {STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => openDeliver(o)} title="Deliver login" aria-label="Deliver login" data-testid={`admin-order-deliver-${o.id}`} className="rounded-full border border-white/15 p-2 text-slate-300 hover:border-[#a259ff] hover:text-[#a259ff]">
                  <Truck className="h-4 w-4" />
                </button>
                <button onClick={() => { setNoteText(o.notes ?? ""); setNoting(o); }} title="Private note" aria-label="Private note" data-testid={`admin-order-notes-${o.id}`} className={`rounded-full border p-2 hover:border-[#a259ff] hover:text-[#a259ff] ${o.notes ? "border-[#a259ff]/60 text-[#a259ff]" : "border-white/15 text-slate-300"}`}>
                  <StickyNote className="h-4 w-4" />
                </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!shown.length && <p className="p-6 text-sm text-slate-400" data-testid="admin-orders-no-match">No orders match.</p>}
      <ProofViewer order={viewing} onClose={() => setViewing(null)} />
      <Dialog open={Boolean(delivering)} onOpenChange={(v) => !v && setDelivering(null)}>
        <DialogContent className="rounded-none border-white/10 bg-[#0b0b11] text-white sm:max-w-lg" data-testid="admin-deliver-dialog">
          <DialogTitle className="font-display text-4xl font-normal uppercase">Deliver {delivering && orderRef(delivering.id)}</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Paste the account login. The buyer sees it on their private order page and gets an email telling them it's ready (the login itself is never emailed).
          </DialogDescription>
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={6} placeholder={"Email: ...\nPassword: ...\nNotes: ..."} data-testid="admin-deliver-details-input" className="w-full border border-white/15 bg-black/40 p-3 font-mono text-sm text-white focus:border-[#a259ff] focus:outline-none" />
          <button disabled={!details.trim()} onClick={() => update(delivering.id, "delivered", details)} data-testid="admin-deliver-submit" className="h-12 bg-[#a259ff] font-mono text-xs font-bold uppercase tracking-widest text-black disabled:opacity-50">
            Mark delivered & notify buyer
          </button>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(noting)} onOpenChange={(v) => !v && setNoting(null)}>
        <DialogContent className="rounded-none border-white/10 bg-[#0b0b11] text-white sm:max-w-lg" data-testid="admin-notes-dialog">
          <DialogTitle className="font-display text-4xl font-normal uppercase">Note · {noting && orderRef(noting.id)}</DialogTitle>
          <DialogDescription className="text-sm text-slate-400">Only you can see this. Buyers never see private notes.</DialogDescription>
          <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} maxLength={2000} rows={5} placeholder="Paid via Cash App, txn 123" data-testid="admin-notes-input" className="w-full border border-white/15 bg-black/40 p-3 text-sm text-white focus:border-[#a259ff] focus:outline-none" />
          <button onClick={saveNotes} data-testid="admin-notes-save" className="h-12 bg-[#a259ff] font-mono text-xs font-bold uppercase tracking-widest text-black">Save note</button>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};
